// src/hooks/queries/useConocimiento.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { conocimientoService } from '@/services/conocimiento';
import type {
  ArticuloFiltros,
  ArticuloCreate,
  ArticuloUpdate,
  CategoriaCreate,
  CategoriaUpdate,
} from '@/types';

// =====================
// CATEGORÍAS
// =====================

export function useCategorias(incluirInactivas: boolean = false) {
  return useQuery({
    queryKey: ['conocimiento', 'categorias', { incluirInactivas }],
    queryFn: () => conocimientoService.categorias.list(incluirInactivas),
  });
}

export function useCategoriasArbol() {
  return useQuery({
    queryKey: ['conocimiento', 'categorias', 'arbol'],
    queryFn: () => conocimientoService.categorias.getArbol(),
  });
}

export function useCategoria(id: string) {
  return useQuery({
    queryKey: ['conocimiento', 'categorias', id],
    queryFn: () => conocimientoService.categorias.get(id),
    enabled: !!id,
  });
}

export function useCrearCategoria() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CategoriaCreate) => conocimientoService.categorias.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'categorias'] });
    },
  });
}

export function useActualizarCategoria() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CategoriaUpdate }) =>
      conocimientoService.categorias.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'categorias'] });
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'categorias', id] });
    },
  });
}

export function useEliminarCategoria() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conocimientoService.categorias.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'categorias'] });
    },
  });
}

// =====================
// ARTÍCULOS CRUD
// =====================

export function useArticulos(params?: ArticuloFiltros) {
  return useQuery({
    queryKey: ['conocimiento', 'articulos', params],
    queryFn: () => conocimientoService.articulos.list(params),
  });
}

export function useArticulo(id: string) {
  return useQuery({
    queryKey: ['conocimiento', 'articulos', id],
    queryFn: () => conocimientoService.articulos.get(id),
    enabled: !!id,
  });
}

export function useArticuloPorNumero(numero: number) {
  return useQuery({
    queryKey: ['conocimiento', 'articulos', 'numero', numero],
    queryFn: () => conocimientoService.articulos.getByNumero(numero),
    enabled: numero > 0,
  });
}

export function useCrearArticulo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ArticuloCreate) => conocimientoService.articulos.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'articulos'] });
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'categorias'] });
    },
  });
}

export function useActualizarArticulo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ArticuloUpdate }) =>
      conocimientoService.articulos.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'articulos'] });
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'articulos', id] });
    },
  });
}

export function useEliminarArticulo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conocimientoService.articulos.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'articulos'] });
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'categorias'] });
    },
  });
}

// =====================
// ARTÍCULOS ACCIONES
// =====================

export function usePublicarArticulo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conocimientoService.acciones.publicar(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'articulos'] });
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'articulos', id] });
    },
  });
}

export function useArchivarArticulo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conocimientoService.acciones.archivar(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'articulos'] });
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'articulos', id] });
    },
  });
}

export function useDespublicarArticulo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conocimientoService.acciones.despublicar(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'articulos'] });
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'articulos', id] });
    },
  });
}

export function useMarcarArticuloUtil() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, util }: { id: string; util: boolean }) =>
      conocimientoService.acciones.marcarUtil(id, util),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'articulos', id] });
    },
  });
}

// =====================
// VERSIONES
// =====================

export function useVersionesArticulo(articuloId: string) {
  return useQuery({
    queryKey: ['conocimiento', 'articulos', articuloId, 'versiones'],
    queryFn: () => conocimientoService.versiones.list(articuloId),
    enabled: !!articuloId,
  });
}

export function useVersionArticulo(articuloId: string, version: number) {
  return useQuery({
    queryKey: ['conocimiento', 'articulos', articuloId, 'versiones', version],
    queryFn: () => conocimientoService.versiones.get(articuloId, version),
    enabled: !!articuloId && version > 0,
  });
}

export function useRevertirVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ articuloId, version }: { articuloId: string; version: number }) =>
      conocimientoService.versiones.revertir(articuloId, version),
    onSuccess: (_, { articuloId }) => {
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'articulos'] });
      queryClient.invalidateQueries({ queryKey: ['conocimiento', 'articulos', articuloId] });
    },
  });
}

// =====================
// BÚSQUEDA
// =====================

export function useBuscarArticulos(q: string, params?: ArticuloFiltros) {
  return useQuery({
    queryKey: ['conocimiento', 'buscar', q, params],
    queryFn: () => conocimientoService.buscar(q, params),
    enabled: q.length >= 2,
  });
}

export function useArticulosRelacionados(articuloId: string, limit: number = 5) {
  return useQuery({
    queryKey: ['conocimiento', 'articulos', articuloId, 'relacionados', limit],
    queryFn: () => conocimientoService.getRelacionados(articuloId, limit),
    enabled: !!articuloId,
  });
}
